import { useContent } from '../../contexts/ContentContext'
import ScrollableSection from '../ui/ScrollableSection'
import SectionLabel from '../ui/SectionLabel'
import LazyImage from '../ui/LazyImage'
import CtaButton from '../ui/CtaButton'
import RichText from '../../utils/richText'

/**
 * BodySection — paper editorial block.
 * Supports archive accent colors on headline, answerLead, body, closing
 * via string | { text, accent } | { text, accent }[].
 */
export default function BodySection({ contentKey = 'body' }) {
  const content = useContent()
  const data =
    content[contentKey] ||
    content.mediaText ||
    content.freedomPuzzle ||
    content.storyPartA ||
    content.storyPartB
  if (!data) return null

  const bodyItems = (data.body || data.linearBody || data.paragraphs || []).map((p) => {
    if (typeof p === 'string') return { parts: p }
    if (p?.parts) return { parts: p.parts }
    if (p?.text && p.accent) return { parts: [{ text: p.text, accent: p.accent }] }
    if (p?.text) return { parts: p.text }
    return null
  }).filter(Boolean)

  const imageUrl = data.imageUrl || data.imageLine || data.image
  const imageAlt = data.imageAlt || data.imageLineAlt || ''

  const answerParts = data.answerLeadParts || data.answerLead
  const closingParts = data.closingParts || data.closing || data.manifestLine
  const headlineAccentParts =
    data.headlineAccentParts ||
    (data.headlineAccent
      ? [{ text: data.headlineAccent, accent: data.headlineAccentTone || 'highlight' }]
      : null)

  return (
    <ScrollableSection
      id={data.id || contentKey}
      data-section="BodySection"
      data-testid={`body-section-${contentKey}`}
      className="bg-color-bg-light"
    >
      <div className="max-w-3xl mx-auto text-center">
        {data.label ? <SectionLabel text={data.label} /> : null}
        {data.headlineBefore != null || data.headline || data.headlineParts ? (
          <h2 className="typography-h2-mobile md:typography-h2 leading-tight heading-body-spacing">
            {data.headlineParts ? (
              <RichText parts={data.headlineParts} />
            ) : data.headlineBefore != null ? (
              <>
                {data.headlineBefore}{' '}
                {headlineAccentParts ? (
                  <RichText parts={headlineAccentParts} />
                ) : null}
              </>
            ) : (
              data.headline
            )}
          </h2>
        ) : null}
        {data.subline ? (
          <p className="typography-body-lg text-color-text mt-4">{data.subline}</p>
        ) : null}
      </div>

      {imageUrl ? (
        <div className="mt-10 md:mt-12 max-w-4xl mx-auto overflow-hidden">
          <LazyImage
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-auto object-cover max-h-[420px]"
          />
        </div>
      ) : null}

      {data.question ? (
        <div className="mt-12 max-w-3xl mx-auto">
          <h3 className="typography-h3-mobile md:typography-h3 text-color-primary leading-snug">
            {data.question}
          </h3>
          {answerParts ? (
            <p className="mt-3 typography-body-lg font-medium">
              <RichText parts={answerParts} />
            </p>
          ) : null}
        </div>
      ) : null}

      {bodyItems.length ? (
        <div className="mt-8 md:mt-10 max-w-3xl mx-auto space-y-5">
          {bodyItems.map((item, i) => (
            <p key={i} className="typography-body-lg leading-relaxed text-color-text">
              <RichText parts={item.parts} />
            </p>
          ))}
        </div>
      ) : null}

      {data.imageChain ? (
        <div className="mt-12 max-w-3xl mx-auto">
          <LazyImage
            src={data.imageChain}
            alt={data.imageChainAlt || ''}
            className="w-full h-auto"
          />
        </div>
      ) : null}

      {closingParts ? (
        <p className="mt-10 max-w-3xl mx-auto text-center typography-h3-mobile md:typography-h3 leading-snug">
          <RichText
            parts={
              typeof closingParts === 'string'
                ? [{ text: closingParts, accent: 'highlight' }]
                : closingParts
            }
          />
        </p>
      ) : null}

      {data.manifestExamples ? (
        <p className="mt-4 max-w-3xl mx-auto text-center typography-body text-color-text">
          {data.manifestExamples}
        </p>
      ) : null}

      {data.cta ? (
        <div className="mt-10 text-center">
          <CtaButton label={data.cta} variant="primary" className="py-4 px-10" />
        </div>
      ) : null}
    </ScrollableSection>
  )
}
